// src/api/client.js
//
// Single canonical axios instance for talking to the On-Screen API.
//
// Hooks and components should migrate to importing { api } /
// { apiForTenant(tenant) } from here instead of recreating axios
// instances or re-deriving base URLs in each call site.
//
// Coexistence: this file is purely additive. The existing
// services/http.js + services/sessionLocal.js helpers keep working
// unchanged so adoption can be incremental.
//
// Auth: every instance has `withCredentials: true` so the browser
// automatically sends the httpOnly `authToken` cookie set on login.
// No Authorization header is added — Phase 2b of the cookie migration
// removed the bearer-from-localStorage code path. On 401 the response
// interceptor scrubs any leftover token from localStorage (for users
// who haven't refreshed since pre-Phase-2a) and routes to /login.

import axios from 'axios';
import { isPublicPath } from '../utils/publicPaths';

const RAW_BASE_URL = (process.env.REACT_APP_API_URL
  || process.env.REACT_APP_BASE_URL
  || ''
).replace(/\/+$/, '');

// Defensive: if the build baked in a localhost URL (typically from a stray
// .env.local that CRA picks up for *all* builds) but the page is served from
// a real origin, fall back to relative URLs so the call stays same-origin.
// Prevents ERR_CONNECTION_REFUSED on phones/remote browsers.
function effectiveBaseUrl(raw) {
  if (typeof window === 'undefined') return raw;
  if (!raw) return raw;
  let host = '';
  try { host = new URL(raw).hostname; } catch { return raw; }
  const baseIsLocal = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(host);
  const pageIsLocal = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(window.location.hostname);
  if (baseIsLocal && !pageIsLocal) return '';
  return raw;
}

const BASE_URL = effectiveBaseUrl(RAW_BASE_URL);

function buildBase(tenant) {
  const apiRoot = `${BASE_URL}/api`;
  return tenant != null && tenant !== '' ? `${apiRoot}/${tenant}` : apiRoot;
}

function attachInterceptors(instance) {
  // No request interceptor — withCredentials:true on the instance below
  // is enough; the browser ships the cookie on every same-origin call.
  instance.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err?.response?.status;
      if (status === 401 && typeof window !== 'undefined') {
        // Best-effort: scrub any pre-cookie-migration leftover token.
        try { localStorage.removeItem('authToken'); } catch { /* ignore */ }
        // Don't redirect from inside a popup window (OAuth callbacks etc.),
        // and never from a public page — a signed-out visitor loading /about
        // or a public event page legitimately has no session.
        if (
          window.top === window.self &&
          !window.location.pathname.startsWith('/login') &&
          !isPublicPath(window.location.pathname)
        ) {
          window.location.assign('/login');
        }
      }
      return Promise.reject(err);
    }
  );
  return instance;
}

/** Shared instance for non-tenant-scoped calls (e.g. /api/auth/login). */
export const api = attachInterceptors(axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true,
  timeout: 30_000,
}));

/** Per-tenant client. Use inside hooks that already know the tenant id:
 *
 *   const events = createClient(tenant);
 *   events.get('/events').then(r => setEvents(r.data));
 */
export function createClient(tenant) {
  return attachInterceptors(axios.create({
    baseURL: buildBase(tenant),
    withCredentials: true,
    timeout: 30_000,
  }));
}

/** Convenience alias matching the AI suggestion's naming. */
export const apiForTenant = createClient;

export const __test__ = { buildBase, BASE_URL };
